import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardMedia,
  Typography,
  Grid,
  Chip,
  Divider,
  IconButton,
  Collapse,
  Link as MuiLink,
  Paper,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  ContentCopy as CopyIcon,
  Check as CheckIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';

/**
 * MetadataCard Component
 *
 * Enhanced display for NFT metadata with W3LE design
 */
const MetadataCard = ({
  name,
  symbol,
  ownerOf,
  tokenURI,
  tokenContent,
  imageURL,
  tokenId,
}) => {
  const [expandedMetadata, setExpandedMetadata] = useState(false);
  const [copiedOwner, setCopiedOwner] = useState(false);
  const [copiedURI, setCopiedURI] = useState(false);

  const handleCopy = (text, setCopied) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const truncateAddress = (address) => {
    if (!address) return '';
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  };

  return (
    <Box sx={{ width: '100%', mt: 4 }}>
      {/* Contract Info Card */}
      <Card sx={{ mb: 3, overflow: 'visible' }}>
        <CardContent>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 700, color: 'primary.main' }}>
            {name || 'Evidencer Contract'}
          </Typography>

          <Grid container spacing={3} sx={{ mt: 1 }}>
            {/* Symbol */}
            {symbol && (
              <Grid item xs={12} sm={6}>
                <Paper elevation={0} sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 2 }}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Symbol
                  </Typography>
                  <Chip
                    label={symbol}
                    color="primary"
                    size="small"
                    sx={{ mt: 1, fontWeight: 600 }}
                  />
                </Paper>
              </Grid>
            )}

            {/* Token ID */}
            {tokenId && (
              <Grid item xs={12} sm={6}>
                <Paper elevation={0} sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 2 }}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Record Number
                  </Typography>
                  <Chip
                    label={`#${tokenId}`}
                    color="secondary"
                    size="small"
                    sx={{ mt: 1, fontWeight: 600 }}
                  />
                </Paper>
              </Grid>
            )}

            {/* Owner Address */}
            {ownerOf && (
              <Grid item xs={12}>
                <Paper elevation={0} sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 2 }}>
                  <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                    Claim Manager Address
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontFamily: 'monospace',
                        fontSize: '0.875rem',
                        wordBreak: 'break-all',
                      }}
                    >
                      {ownerOf}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => handleCopy(ownerOf, setCopiedOwner)}
                      sx={{ ml: 'auto' }}
                    >
                      {copiedOwner ? <CheckIcon fontSize="small" color="success" /> : <CopyIcon fontSize="small" />}
                    </IconButton>
                  </Box>
                </Paper>
              </Grid>
            )}

            {/* Metadata Link */}
            {tokenURI && (
              <Grid item xs={12}>
                <Paper elevation={0} sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 2 }}>
                  <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                    Evidencer Metadata Link
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <MuiLink
                      href={tokenURI}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        fontSize: '0.875rem',
                        wordBreak: 'break-all',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                      }}
                    >
                      {truncateAddress(tokenURI)}
                      <OpenInNewIcon fontSize="small" />
                    </MuiLink>
                    <IconButton
                      size="small"
                      onClick={() => handleCopy(tokenURI, setCopiedURI)}
                      sx={{ ml: 'auto' }}
                    >
                      {copiedURI ? <CheckIcon fontSize="small" color="success" /> : <CopyIcon fontSize="small" />}
                    </IconButton>
                  </Box>
                </Paper>
              </Grid>
            )}
          </Grid>
        </CardContent>
      </Card>

      {/* Image Card */}
      {imageURL && (
        <Card sx={{ mb: 3 }}>
          <CardMedia
            component="img"
            image={imageURL}
            alt="Evidencer Content"
            sx={{
              width: '100%',
              height: 'auto',
              maxHeight: 600,
              objectFit: 'contain',
              bgcolor: 'action.hover',
            }}
          />
        </Card>
      )}

      {/* Metadata Content Card */}
      {tokenContent && (
        <Card>
          <CardContent>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 2,
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Metadata Content
              </Typography>
              <IconButton onClick={() => setExpandedMetadata(!expandedMetadata)}>
                {expandedMetadata ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Box>

            <Collapse in={expandedMetadata}>
              <Divider sx={{ mb: 2 }} />
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  bgcolor: 'action.hover',
                  borderRadius: 2,
                  maxHeight: 400,
                  overflow: 'auto',
                }}
              >
                <pre
                  style={{
                    margin: 0,
                    fontFamily: 'monospace',
                    fontSize: '0.875rem',
                    whiteSpace: 'pre-wrap',
                    wordWrap: 'break-word',
                  }}
                >
                  {JSON.stringify(tokenContent, null, 2)}
                </pre>
              </Paper>
            </Collapse>

            {!expandedMetadata && (
              <Typography variant="body2" color="text.secondary">
                Click to expand and view full metadata
              </Typography>
            )}
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default MetadataCard;
